import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Search, FileText, Loader2, Edit3, Globe, BadgeCheck, FileEdit, Archive, Trash2, Upload } from "lucide-react";
import API from "../lib/api";
import AdminGuard from "../components/admin/AdminGuard";
import AdminShell from "../components/admin/AdminShell";
import { errorMessage } from "../lib/errors";

const STATUS_META = {
  draft: { tone: "bg-white/10 text-white/80 border-white/15", icon: FileEdit },
  published: { tone: "bg-[#C8A24A]/15 text-[#E5C76B] border-[#C8A24A]/40", icon: BadgeCheck },
  archived: { tone: "bg-rose-500/15 text-rose-300 border-rose-500/30", icon: Archive },
};

function StatusPill({ status }) {
  const { t } = useTranslation();
  const meta = STATUS_META[status] || STATUS_META.draft;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${meta.tone}`}>
      <Icon className="w-3 h-3" />
      {t(`admin.content.status.${status}`, status)}
    </span>
  );
}

export default function AdminContentListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const [filters, setFilters] = useState({ status: "", topic: "", q: "" });
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("type", "article");
        if (filters.status) params.set("status", filters.status);
        if (filters.topic) params.set("topic", filters.topic);
        if (filters.q) params.set("q", filters.q);
        params.set("limit", "100");
        const { data } = await API.get(`/admin/content?${params.toString()}`);
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch (e) {
        if (!cancelled) toast.error(errorMessage(e, t("admin.content.load_failed", "Couldn\u2019t load content")));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshTick, filters, t]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters((f) => ({ ...f, q: searchInput.trim() }));
  };

  const handleStatusFilter = (status) => {
    setFilters((f) => ({ ...f, status: f.status === status ? "" : status }));
  };

  const handleArchive = async (item) => {
    if (!window.confirm(t("admin.content.confirm_archive", "Archive '{{slug}}'? You can restore it later.", { slug: item.slug }))) return;
    try {
      await API.delete(`/admin/content/${item.content_id}`);
      toast.success(t("admin.content.archive_success", "Archived '{{slug}}'", { slug: item.slug }));
      setRefreshTick((n) => n + 1);
    } catch (err) {
      toast.error(errorMessage(err, t("admin.content.archive_failed", "Couldn\u2019t archive content")));
    }
  };

  const counts = items.reduce((acc, it) => { acc[it.status] = (acc[it.status] || 0) + 1; return acc; }, {});

  return (
    <AdminGuard>
      <AdminShell active="content">
        <div className="space-y-6" data-testid="admin-content-list">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="eyebrow mb-2">{t("admin.content.eyebrow", "\u2014 Editorial")}</div>
              <h2 className="font-display text-2xl md:text-3xl leading-tight" data-testid="admin-content-title">
                {t("admin.content.title", "Content Manager")}
              </h2>
              <p className="text-sm text-white/65 mt-2 max-w-2xl leading-relaxed">
                {t("admin.content.subtitle", "Create, edit, and publish multilingual articles powering the DONiTSH!T ecosystem. English is required; other languages fall back to English when missing.")}
              </p>
            </div>
            <div className="flex items-center gap-2 self-start">
              <button
                onClick={() => navigate("/admin/content/import")}
                className="btn-ghost text-sm inline-flex items-center gap-2"
                data-testid="import-content-btn"
              >
                <Upload className="w-4 h-4" />
                {t("admin.content.import_btn", "Import")}
              </button>
              <button
                onClick={() => navigate("/admin/content/new")}
                className="btn-primary text-sm inline-flex items-center gap-2"
                data-testid="new-content-btn"
              >
                <Plus className="w-4 h-4" />
                {t("admin.content.new_btn", "New article")}
              </button>
            </div>
          </div>

          {/* Toolbar */}
          <div className="card-surface p-4">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              {/* Status filter chips */}
              <div className="flex items-center gap-2 flex-wrap">
                {["draft", "published", "archived"].map((s) => {
                  const active = filters.status === s;
                  const meta = STATUS_META[s];
                  const Icon = meta.icon;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => handleStatusFilter(s)}
                      className={`text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors ${
                        active ? meta.tone : "text-white/65 border-white/15 hover:border-white/30"
                      }`}
                      data-testid={`status-filter-${s}`}
                    >
                      <Icon className="w-3 h-3" />
                      {t(`admin.content.status.${s}`, s)}
                      <span className="opacity-60">({counts[s] || 0})</span>
                    </button>
                  );
                })}
              </div>
              {/* Search */}
              <form onSubmit={handleSearch} className="flex items-center gap-2" data-testid="search-form">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                  <input
                    type="text"
                    placeholder={t("admin.content.search_placeholder", "Search by title, slug, tag\u2026")}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-64 bg-[#0F1116] border border-white/10 rounded-lg ps-9 pe-3 py-2 text-sm focus:outline-none focus:border-[#C8A24A]/40 transition-colors"
                    data-testid="search-input"
                  />
                </div>
                {filters.q && (
                  <button
                    type="button"
                    onClick={() => { setFilters((f) => ({ ...f, q: "" })); setSearchInput(""); }}
                    className="text-xs text-white/55 hover:text-white"
                    data-testid="clear-search"
                  >
                    {t("admin.content.clear", "Clear")}
                  </button>
                )}
              </form>
            </div>
          </div>

          {/* List */}
          <div className="card-surface" data-testid="content-list">
            {loading ? (
              <div className="p-8 text-center text-sm text-white/60 inline-flex items-center justify-center gap-2 w-full">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("admin.loading", "Loading\u2026")}
              </div>
            ) : items.length === 0 ? (
              <div className="p-10 text-center" data-testid="content-empty">
                <FileText className="w-10 h-10 mx-auto gold-text opacity-60 mb-3" />
                <div className="font-display text-lg mb-1">{t("admin.content.empty_title", "No content yet")}</div>
                <p className="text-sm text-white/55 mb-4">{t("admin.content.empty_sub", "Create your first article to get started.")}</p>
                <button onClick={() => navigate("/admin/content/new")} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="empty-cta">
                  <Plus className="w-4 h-4" />
                  {t("admin.content.new_btn", "New article")}
                </button>
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {items.map((it) => (
                  <li key={it.content_id} className="p-4 hover:bg-white/[0.02] transition-colors" data-testid={`content-row-${it.slug}`}>
                    <div className="flex items-start justify-between gap-4">
                      <Link to={`/admin/content/${it.content_id}/edit`} className="flex-1 min-w-0 group">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-display text-base group-hover:gold-text transition-colors truncate">
                            {it.title_by_locale?.en || it.slug}
                          </span>
                          <StatusPill status={it.status} />
                          {it.source === "static_migration" && (
                            <span className="text-[10px] uppercase tracking-wider gold-text border border-[#C8A24A]/40 rounded px-1.5 py-0.5">
                              {t("admin.content.source_migrated", "Migrated")}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-white/55 mb-1.5">
                          <span className="text-white/70">/{it.slug}</span>
                          {it.topic ? <> · <span>{it.topic}</span></> : null}
                          {it.tags && it.tags.length > 0 ? <> · <span>{it.tags.slice(0, 4).join(", ")}{it.tags.length > 4 ? "\u2026" : ""}</span></> : null}
                        </div>
                        <div className="text-xs text-white/45 flex items-center gap-2 flex-wrap">
                          <Globe className="w-3 h-3" />
                          <span>{(it.available_locales || []).join(", ") || "en"}</span>
                          <span className="text-white/30">·</span>
                          <span>{t("admin.content.updated", "Updated")} {(it.updated_at || "").replace("T", " ").slice(0, 19)}</span>
                        </div>
                      </Link>
                      <div className="flex items-center gap-1 shrink-0">
                        <Link
                          to={`/admin/content/${it.content_id}/edit`}
                          className="btn-ghost p-2"
                          aria-label={t("admin.content.edit", "Edit")}
                          data-testid={`edit-${it.slug}`}
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </Link>
                        {it.status !== "archived" && (
                          <button
                            type="button"
                            onClick={() => handleArchive(it)}
                            className="btn-ghost p-2 text-rose-300 hover:text-rose-200"
                            aria-label={t("admin.content.archive", "Archive")}
                            data-testid={`archive-${it.slug}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {!loading && items.length > 0 && (
              <div className="px-4 py-3 border-t border-white/5 text-xs text-white/45 flex items-center justify-between" data-testid="content-footer">
                <span>{t("admin.content.showing", "Showing {{count}} of {{total}}", { count: items.length, total })}</span>
                <span>{t("admin.content.fallback_note", "English serves as fallback for other locales when empty.")}</span>
              </div>
            )}
          </div>
        </div>
      </AdminShell>
    </AdminGuard>
  );
}
